import React, { useState } from 'react';
import type { ToolBlock } from '@/types';

interface ToolCallVisualizationProps {
  toolBlocks: ToolBlock[];
  isAdmin?: boolean;
}

// Map technical tool names to human-readable descriptions
const getToolDisplayName = (toolName: string): string => {
  const toolNameMap: Record<string, string> = {
    'asai_search_company_press_releases': 'Searching company press releases',
    'asai_search_companies': 'Searching companies',
    'asai_get_company_info': 'Getting company information',
    'asai_get_company_financials': 'Retrieving financial data',
    'asai_search_earnings_calls': 'Searching earnings calls',
    'asai_get_earnings_call_transcript': 'Fetching earnings call transcript',
    'asai_search_sec_filings': 'Searching SEC filings',
    'asai_get_sec_filing': 'Retrieving SEC filing',
    'asai_search_events': 'Searching corporate events',
    'asai_get_stock_quote': 'Getting stock quote',
    'asai_get_stock_history': 'Fetching stock price history',
    'asai_search_news': 'Searching news articles',
    'asai_universal_search': 'Searching financial data',
  };

  return toolNameMap[toolName] || toolName.replace(/_/g, ' ').replace(/^asai /, 'ASAI: ');
};

// Generate a short summary from the result and input
const getResultSummary = (result: string | undefined, input: any, toolName: string): string => {
  if (!result) return 'Processing...';

  try {
    const parsed = JSON.parse(result);

    // Extract search term from input if available
    let searchContext = '';
    if (input) {
      const inputObj = typeof input === 'string' ? JSON.parse(input) : input;
      if (inputObj.query) {
        searchContext = `for "${inputObj.query}"`;
      } else if (inputObj.ticker) {
        searchContext = `for ${inputObj.ticker}`;
      } else if (inputObj.company_name) {
        searchContext = `for ${inputObj.company_name}`;
      } else if (inputObj.symbol) {
        searchContext = `for ${inputObj.symbol}`;
      }
    }

    // Handle different types of results with context
    if (Array.isArray(parsed)) {
      const count = parsed.length;
      if (toolName.includes('search')) {
        return `Searched ${searchContext} - found ${count} result${count !== 1 ? 's' : ''}`;
      }
      return `Retrieved ${count} item${count !== 1 ? 's' : ''}`;
    }

    if (parsed.count !== undefined) {
      if (toolName.includes('search')) {
        return `Searched ${searchContext} - found ${parsed.count} result${parsed.count !== 1 ? 's' : ''}`;
      }
      return `Found ${parsed.count} item${parsed.count !== 1 ? 's' : ''}`;
    }

    if (parsed.results && Array.isArray(parsed.results)) {
      const count = parsed.results.length;
      if (toolName.includes('search')) {
        return `Searched ${searchContext} - found ${count} result${count !== 1 ? 's' : ''}`;
      }
      return `Retrieved ${count} item${count !== 1 ? 's' : ''}`;
    }

    if (parsed.success) {
      return searchContext ? `Retrieved ${searchContext}` : 'Retrieved successfully';
    }

    // For non-array results, try to extract meaningful info
    if (parsed.company || parsed.ticker || parsed.symbol) {
      return `Retrieved ${parsed.company || parsed.ticker || parsed.symbol}`;
    }

    // Default: Completed
    return 'Completed';
  } catch {
    // If not JSON, return completed
    return 'Completed';
  }
};

export const ToolCallVisualization: React.FC<ToolCallVisualizationProps> = ({ toolBlocks, isAdmin = false }) => {
  const [expandedTools, setExpandedTools] = useState<Set<string>>(new Set());

  const toggleTool = (toolId: string) => {
    const newExpanded = new Set(expandedTools);
    if (newExpanded.has(toolId)) {
      newExpanded.delete(toolId);
    } else {
      newExpanded.add(toolId);
    }
    setExpandedTools(newExpanded);
  };

  if (!toolBlocks || toolBlocks.length === 0) {
    return null;
  }

  return (
    <div className="my-3 space-y-2">
      {toolBlocks.map((block, index) => {
        const isExpanded = expandedTools.has(block.tool.id);
        const isProcessing = !block.result;
        const displayName = getToolDisplayName(block.tool.name);
        const resultSummary = getResultSummary(block.result, block.tool.input, block.tool.name);

        return (
          <div
            key={`${block.tool.id}-${index}`}
            className="border border-blue-200 rounded-lg overflow-hidden bg-blue-50"
          >
            {/* Tool Use Header */}
            <div className="w-full px-4 py-3 flex items-center justify-between">
              <div className="flex items-center space-x-3 flex-1">
                {/* ASAI Logo with animation when processing */}
                <div className="w-8 h-8 flex items-center justify-center flex-shrink-0">
                  <img
                    src="https://app.asai.alphastreet.io/assets/images/logo.svg"
                    alt="ASAI"
                    className={`w-8 h-8 ${isProcessing ? 'animate-flash' : ''}`}
                  />
                </div>

                {/* Tool Name and Summary */}
                <div className="text-left flex-1">
                  <div className="font-semibold text-blue-900 text-sm">
                    {displayName}
                  </div>
                  <div className="text-xs text-blue-600">
                    {resultSummary}
                  </div>
                </div>
              </div>

              {/* Expand/Collapse Icon - Only show for admin users */}
              {isAdmin && (
                <button
                  onClick={() => toggleTool(block.tool.id)}
                  className="p-2 hover:bg-blue-100 rounded transition-colors"
                  title="View technical details"
                >
                  <svg
                    className={`w-5 h-5 text-blue-600 transition-transform ${isExpanded ? 'rotate-180' : ''}`}
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </button>
              )}
            </div>

            {/* Expanded Content */}
            {isExpanded && (
              <div className="px-4 pb-3 border-t border-blue-200">
                {/* Tool Input */}
                <div className="mt-3">
                  <div className="text-xs font-semibold text-blue-700 mb-2">Input:</div>
                  <div className="bg-white rounded p-3 text-xs">
                    <pre className="whitespace-pre-wrap text-gray-800 font-mono">
                      {block.tool.input
                        ? (typeof block.tool.input === 'string'
                            ? block.tool.input
                            : JSON.stringify(block.tool.input, null, 2))
                        : '(No input parameters)'}
                    </pre>
                  </div>
                </div>

                {/* Tool Result (if available) */}
                {block.result && (
                  <div className="mt-3">
                    <div className="text-xs font-semibold text-blue-700 mb-2">Result:</div>
                    <div className="bg-white rounded p-3 text-xs">
                      <pre className="whitespace-pre-wrap text-gray-800 font-mono max-h-48 overflow-y-auto">
                        {block.result}
                      </pre>
                    </div>
                  </div>
                )}

                {/* Timestamp */}
                <div className="mt-2 text-xs text-blue-600">
                  {block.timestamp.toLocaleTimeString()}
                </div>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};
